import React, { useState, useEffect } from 'react'
import { useAuthStore } from '../stores/authStore'
import axios from 'axios'
import toast from 'react-hot-toast'
import { handlePermissionError } from '../utils/permissionErrorHandler'

interface Service {
  id: string
  name: string
  description: string
  serviceType: string
  serviceCategory: string
  priceGbp: number | string
  billingCycleDays?: number
  predictedTimeline?: number
  setupTimeDays?: number
  features: any
  isActive: boolean
  sortOrder: number
  paymentType?: string
  isDeletable?: boolean
}

interface ClientOrganization {
  id: string
  name: string
  domain: string
  isActive: boolean
}

interface ClientService {
  id: string
  serviceNumber: string
  status: string
  startDate: string
  endDate?: string
  currentCycle: number
  totalCycles: number
  // Independent client service fields
  name: string
  description?: string
  serviceType: string
  serviceCategory: string
  priceGbp: number | string
  billingCycleDays?: number
  predictedTimeline?: number
  setupTimeDays?: number
  // Client org relation
  clientOrg: {
    id: string
    name: string
    domain: string
  }
  assignedStaff?: {
    id: string
    firstName: string
    lastName: string
    email: string
  }
}

const ServiceManagement: React.FC = () => {
  const { user } = useAuthStore()
  
  const [services, setServices] = useState<Service[]>([])
  const [clientOrganizations, setClientOrganizations] = useState<ClientOrganization[]>([])
  const [clientServices, setClientServices] = useState<ClientService[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [activeTab, setActiveTab] = useState<'services' | 'assignments' | 'overview' | 'lifecycle'>('overview')
  
  // Lifecycle Management State
  const [expiringServices, setExpiringServices] = useState<ClientService[]>([])
  const [servicesNeedingRenewal, setServicesNeedingRenewal] = useState<ClientService[]>([])
  const [lifecycleAnalytics, setLifecycleAnalytics] = useState<any>(null)
  const [cronStatus, setCronStatus] = useState<any>(null)
  
  // Service Management
  const [showServiceForm, setShowServiceForm] = useState(false)
  const [editingService, setEditingService] = useState<Service | null>(null)
  const [serviceForm, setServiceForm] = useState({
    name: '',
    description: '',
    serviceType: '',
    serviceCategory: 'SUBSCRIPTION',
    priceGbp: '',
    billingCycleDays: '28',
    predictedTimeline: '',
    setupTimeDays: '',
    features: {},
    sortOrder: '0',
    paymentType: 'recurring',
    isDeletable: true
  })

  // Service Assignment
  const [showAssignmentForm, setShowAssignmentForm] = useState(false)
  const [assignmentForm, setAssignmentForm] = useState({
    clientOrgId: '',
    serviceId: '',
    startDate: '',
    endDate: '',
    totalCycles: '13',
    servicePriceId: ''
  })
  const [servicePrices, setServicePrices] = useState<any[]>([])
  const [showPriceModal, setShowPriceModal] = useState(false)
  const [priceForm, setPriceForm] = useState({ nickname: '', priceGbp: '', billingCycleDays: '' })

  // Edit cycles modal
  const [showEditCycles, setShowEditCycles] = useState(false)
  const [editCyclesForm, setEditCyclesForm] = useState<{ id: string; totalCycles: string }>({ id: '', totalCycles: '' })

  const isStaff = user?.roles?.some(role => ['administrator', 'support_staff', 'manager', 'staff'].includes(role))

  useEffect(() => {
    if (isStaff) {
      fetchData()
    }
  }, [isStaff])

  // Load price variations when a service is selected
  useEffect(() => {
    const loadPrices = async () => {
      if (!assignmentForm.serviceId) { setServicePrices([]); return }
      try {
        const res = await axios.get(`/api/services/services/${assignmentForm.serviceId}/prices`)
        if (res.data.success) setServicePrices(res.data.data || [])
      } catch (e) {
        setServicePrices([])
      }
    }
    loadPrices()
  }, [assignmentForm.serviceId])

  const fetchData = async () => {
    try {
      setIsLoading(true)
      console.log('🔍 Fetching service management data...')
      
      const [servicesRes, orgsRes, clientServicesRes, expiringRes, renewalRes, analyticsRes, cronRes] = await Promise.all([
        axios.get('/api/services/services'),
        axios.get('/api/client-organizations'),
        axios.get('/api/services/client-services'),
        axios.get('/api/services/lifecycle/expiring-soon'),
        axios.get('/api/services/lifecycle/needing-renewal'),
        axios.get('/api/services/lifecycle/analytics'),
        axios.get('/api/services/cron/status')
      ])

      console.log('📊 API Responses:', {
        services: servicesRes.data,
        orgs: orgsRes.data,
        clientServices: clientServicesRes.data
      })

      if (servicesRes.data.success) {
        setServices(servicesRes.data.data || [])
        console.log('✅ Services loaded:', servicesRes.data.data?.length || 0)
      } else {
        console.error('❌ Services API failed:', servicesRes.data)
      }
      
      if (orgsRes.data.success) {
        setClientOrganizations(orgsRes.data.data || [])
        console.log('✅ Client organizations loaded:', orgsRes.data.data?.length || 0)
      } else {
        console.error('❌ Client organizations API failed:', orgsRes.data)
      }
      
      if (clientServicesRes.data.success) {
        setClientServices(clientServicesRes.data.data || [])
        console.log('✅ Client services loaded:', clientServicesRes.data.data?.length || 0)
      } else {
        console.error('❌ Client services API failed:', clientServicesRes.data)
      }

      // Lifecycle data processing
      if (expiringRes.data.success) {
        setExpiringServices(expiringRes.data.data || [])
        console.log('✅ Expiring services loaded:', expiringRes.data.data?.length || 0)
      }

      if (renewalRes.data.success) {
        setServicesNeedingRenewal(renewalRes.data.data || [])
        console.log('✅ Services needing renewal loaded:', renewalRes.data.data?.length || 0)
      }

      if (analyticsRes.data.success) {
        setLifecycleAnalytics(analyticsRes.data.data)
        console.log('✅ Lifecycle analytics loaded')
      }

      if (cronRes.data.success) {
        setCronStatus(cronRes.data.data)
        console.log('✅ Cron status loaded')
      }
    } catch (error) {
      console.error('❌ Error fetching service management data:', error)
      
      // Handle permission errors specifically
      if (handlePermissionError(error, 'manage services')) {
        // Permission error handled by the handler
      } else {
        toast.error('Failed to load service data')
      }
    } finally {
      setIsLoading(false)
    }
  }

  const handleCreateService = async (e: React.FormEvent) => {
    e.preventDefault()
    try {
      const response = await axios.post('/api/services/services', serviceForm)
      if (response.data.success) {
        toast.success('Service created successfully')
        setShowServiceForm(false)
        setServiceForm({
          name: '',
          description: '',
          serviceType: '',
          serviceCategory: 'SUBSCRIPTION',
          priceGbp: '',
          billingCycleDays: '28',
          predictedTimeline: '',
          setupTimeDays: '',
          features: {},
          sortOrder: '0',
          paymentType: 'recurring',
          isDeletable: true
        })
        fetchData()
      }
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to create service')
    }
  }

  const handleEditService = (service: Service) => {
    setEditingService(service)
    setServiceForm({
      name: service.name,
      description: service.description || '',
      serviceType: service.serviceType,
      serviceCategory: service.serviceCategory,
      priceGbp: service.priceGbp.toString(),
      billingCycleDays: service.billingCycleDays?.toString() || '',
      predictedTimeline: service.predictedTimeline?.toString() || '',
      setupTimeDays: service.setupTimeDays?.toString() || '',
      features: service.features || {},
      sortOrder: service.sortOrder.toString(),
      paymentType: service.paymentType || 'recurring',
      isDeletable: service.isDeletable !== false
    })
    setShowServiceForm(true)
  }

  const handleDeleteService = async (service: Service) => {
    if (!window.confirm(`Are you sure you want to delete "${service.name}"? This action cannot be undone.`)) {
      return
    }

    try {
      const response = await axios.delete(`/api/services/services/${service.id}`)
      if (response.data.success) {
        toast.success('Service deleted successfully')
        fetchData()
      }
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to delete service')
    }
  }

  const handleUpdateService = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!editingService) return

    try {
      const response = await axios.put(`/api/services/services/${editingService.id}`, serviceForm)
      if (response.data.success) {
        toast.success('Service updated successfully')
        setShowServiceForm(false)
        setEditingService(null)
        setServiceForm({
          name: '',
          description: '',
          serviceType: '',
          serviceCategory: 'SUBSCRIPTION',
          priceGbp: '',
          billingCycleDays: '28',
          predictedTimeline: '',
          setupTimeDays: '',
          features: {},
          sortOrder: '0',
          paymentType: 'recurring',
          isDeletable: true
        })
        fetchData()
      }
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to update service')
    }
  }

  const handleAssignService = async (e: React.FormEvent) => {
    e.preventDefault()
    try {
      const response = await axios.post(`/api/organizations/${assignmentForm.clientOrgId}/services`, {
        serviceId: assignmentForm.serviceId,
        startDate: assignmentForm.startDate,
        endDate: assignmentForm.endDate || null,
        totalCycles: parseInt(assignmentForm.totalCycles),
        servicePriceId: assignmentForm.servicePriceId || undefined
      })
      
      if (response.data.success) {
        toast.success('Service assigned successfully')
        setShowAssignmentForm(false)
        setAssignmentForm({
          clientOrgId: '',
          serviceId: '',
          startDate: '',
          endDate: '',
          totalCycles: '13',
          servicePriceId: ''
        })
        fetchData()
      }
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to assign service')
    }
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    })
  }

  const formatPrice = (price: number | string) => {
    const numPrice = typeof price === 'string' ? parseFloat(price) : price
    return `£${numPrice.toFixed(2)}`
  }

  // Derive current cycle from startDate and billingCycleDays to match client view
  const deriveCurrentCycle = (service: ClientService): number => {
    try {
      const start = new Date(service.startDate)
      const now = new Date()
      const msPerDay = 1000 * 60 * 60 * 24
      const daysSinceStart = Math.floor((now.getTime() - start.getTime()) / msPerDay)
      const cycleDays = service.billingCycleDays || 28
      const calculated = Math.floor(daysSinceStart / cycleDays) + 1
      if (!isFinite(calculated)) return service.currentCycle || 1
      return Math.min(Math.max(1, calculated), service.totalCycles)
    } catch {
      return service.currentCycle || 1
    }
  }

  // Lifecycle Management Handlers
  const handleRenewService = async (serviceId: string) => {
    try {
      await axios.post(`/api/services/client-services/${serviceId}/renew`, {
        extendCycles: 13
      })
      toast.success('Service renewed successfully')
      fetchData()
    } catch (error) {
      console.error('Renew service error:', error)
      toast.error('Failed to renew service')
    }
  }

  const handleAdvanceCycle = async (serviceId: string) => {
    try {
      await axios.post(`/api/services/client-services/${serviceId}/advance-cycle`)
      toast.success('Service advanced to next cycle')
      fetchData()
    } catch (error) {
      console.error('Advance cycle error:', error)
      toast.error('Failed to advance cycle')
    }
  }

  const handleUpdateCycle = async (serviceId: string) => {
    try {
      await axios.post(`/api/services/client-services/${serviceId}/update-cycle`)
      toast.success('Cycle information updated')
      fetchData()
    } catch (error) {
      console.error('Update cycle error:', error)
      toast.error('Failed to update cycle information')
    }
  }

  const handleTriggerDailyUpdate = async () => {
    try {
      await axios.post('/api/services/cron/trigger-daily-update')
      toast.success('Daily update triggered successfully')
      fetchData()
    } catch (error) {
      console.error('Trigger daily update error:', error)
      toast.error('Failed to trigger daily update')
    }
  }

  const handleTriggerExpirationCheck = async () => {
    try {
      await axios.post('/api/services/cron/trigger-expiration-check')
      toast.success('Expiration check triggered successfully')
      fetchData()
    } catch (error) {
      console.error('Trigger expiration check error:', error)
      toast.error('Failed to trigger expiration check')
    }
  }

  const handleTriggerWeeklyReport = async () => {
    try {
      await axios.post('/api/services/cron/trigger-weekly-report')
      toast.success('Weekly report triggered successfully')
    } catch (error) {
      console.error('Trigger weekly report error:', error)
      toast.error('Failed to trigger weekly report')
    }
  }

  if (!isStaff) {
    console.log('🚫 Access denied - not staff user')
    return (
      <div className="px-4 sm:px-6 lg:px-8">
        <div className="text-center py-12">
          <h1 className="text-2xl font-semibold text-gray-900">Access Denied</h1>
          <p className="mt-2 text-gray-600">Service management is restricted to staff members only.</p>
        </div>
      </div>
    )
  }

  if (isLoading) {
    console.log('⏳ Loading service management data...')
    return (
      <div className="px-4 sm:px-6 lg:px-8">
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading service data...</p>
        </div>
      </div>
    )
  }

  console.log('🎯 Rendering ServiceManagement component:', {
    services: services.length,
    clientOrganizations: clientOrganizations.length,
    clientServices: clientServices.length,
    activeTab
  })

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      <div className="sm:flex sm:items-center">
        <div className="sm:flex-auto">
          <h1 className="text-3xl font-semibold text-gray-900">Service Management</h1>
          <p className="mt-2 text-sm text-gray-700">
            Manage services and assign services to client organizations
          </p>
        </div>
        <div className="mt-4 sm:mt-0 sm:ml-16 sm:flex-none">
          <div className="flex space-x-3">
            <button
              onClick={() => setShowServiceForm(true)}
              className="btn-primary"
            >
              Create Service
            </button>
            <button
              onClick={() => setShowAssignmentForm(true)}
              className="btn-secondary"
            >
              Assign Service
            </button>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="mt-8">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8">
            {[
              { id: 'overview', name: 'Overview' },
              { id: 'services', name: 'Services' },
              { id: 'assignments', name: 'Assignments' },
              { id: 'lifecycle', name: 'Lifecycle' }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as any)}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === tab.id
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {tab.name}
              </button>
            ))}
          </nav>
        </div>
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && (
        <div className="mt-8 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
          <div className="card">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-blue-100 rounded-md flex items-center justify-center">
                  <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                  </svg>
                </div>
              </div>
              <div className="ml-4">
                <h3 className="text-lg font-medium text-gray-900">Services</h3>
                <p className="text-2xl font-semibold text-gray-900">{services.length}</p>
                <p className="text-sm text-gray-500">Available service packages</p>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-green-100 rounded-md flex items-center justify-center">
                  <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                </div>
              </div>
              <div className="ml-4">
                <h3 className="text-lg font-medium text-gray-900">Client Organizations</h3>
                <p className="text-2xl font-semibold text-gray-900">{clientOrganizations.length}</p>
                <p className="text-sm text-gray-500">Active client organizations</p>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-purple-100 rounded-md flex items-center justify-center">
                  <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
              </div>
              <div className="ml-4">
                <h3 className="text-lg font-medium text-gray-900">Active Services</h3>
                <p className="text-2xl font-semibold text-gray-900">{clientServices.length}</p>
                <p className="text-sm text-gray-500">Currently assigned services</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Services Tab */}
      {activeTab === 'services' && (
        <div className="mt-8">
          <div className="card">
            <div className="px-4 py-5 sm:p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Services</h3>
              <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
                <table className="min-w-full divide-y divide-gray-300">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Category</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Price</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Timeline</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {services.map((service) => (
                      <tr key={service.id}>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div>
                            <div className="text-sm font-medium text-gray-900">{service.name}</div>
                            <div className="text-sm text-gray-500">{service.description}</div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{service.serviceType}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{service.serviceCategory}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{formatPrice(service.priceGbp)}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {service.serviceCategory === 'SUBSCRIPTION' && service.billingCycleDays ? `${service.billingCycleDays} days` : 
                           service.serviceCategory === 'PROJECT' && service.predictedTimeline ? `${service.predictedTimeline} days` :
                           service.serviceCategory === 'PRODUCT' && service.setupTimeDays ? `${service.setupTimeDays} days` : 'N/A'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            service.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                          }`}>
                            {service.isActive ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <div className="flex space-x-2">
                            <button
                              onClick={() => handleEditService(service)}
                              className="text-indigo-600 hover:text-indigo-900"
                            >
                              Edit
                            </button>
                            <button
                              onClick={() => handleDeleteService(service)}
                              className="text-red-600 hover:text-red-900"
                              disabled={!service.isDeletable}
                            >
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Assignments Tab */}
      {activeTab === 'assignments' && (
        <div className="mt-8">
          <div className="card">
            <div className="px-4 py-5 sm:p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Service Assignments</h3>
              <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
                <table className="min-w-full divide-y divide-gray-300">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Client Organization</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Service Number</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Service</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Start Date</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Cycle</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {clientServices.map((service) => (
                      <tr key={service.id}>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div>
                            <div className="text-sm font-medium text-gray-900">{service.clientOrg.name}</div>
                            <div className="text-sm text-gray-500">{service.clientOrg.domain}</div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900 font-mono">{service.serviceNumber}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div>
                            <div className="text-sm font-medium text-gray-900">{service.name}</div>
                            <div className="text-sm text-gray-500">{formatPrice(service.priceGbp)}</div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{formatDate(service.startDate)}</td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            service.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                          }`}>
                            {service.status.charAt(0).toUpperCase() + service.status.slice(1)}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          <div className="flex items-center gap-3">
                            <span>{deriveCurrentCycle(service)} / {service.totalCycles}</span>
                            <button
                              className="text-xs text-blue-600 hover:text-blue-800"
                              onClick={() => {
                                setEditCyclesForm({ id: service.id, totalCycles: String(service.totalCycles ?? '') })
                                setShowEditCycles(true)
                              }}
                            >
                              Edit
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Service Form Modal */}
      {showServiceForm && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-medium text-gray-900">
                  {editingService ? 'Edit Service' : 'Create Service'}
                </h3>
                <button
                  onClick={() => {
                    setShowServiceForm(false)
                    setEditingService(null)
                    setServiceForm({
                      name: '',
                      description: '',
                      serviceType: '',
                      serviceCategory: 'SUBSCRIPTION',
                      priceGbp: '',
                      billingCycleDays: '28',
                      predictedTimeline: '',
                      setupTimeDays: '',
                      features: {},
                      sortOrder: '0',
                      paymentType: 'recurring',
                      isDeletable: true
                    })
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              
              <form onSubmit={editingService ? handleUpdateService : handleCreateService} className="space-y-4">
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Name</label>
                    <input
                      type="text"
                      required
                      className="input-field mt-1"
                      value={serviceForm.name}
                      onChange={(e) => setServiceForm(prev => ({ ...prev, name: e.target.value }))}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Service Type</label>
                    <select
                      required
                      className="input-field mt-1"
                      value={serviceForm.serviceType}
                      onChange={(e) => setServiceForm(prev => ({ ...prev, serviceType: e.target.value }))}
                    >
                      <option value="">Select Type</option>
                      <option value="SMM">Social Media Management</option>
                      <option value="Website">Website Development</option>
                      <option value="SEO">Search Engine Optimization</option>
                      <option value="Design">Design Services</option>
                      <option value="Support">Technical Support</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Service Category</label>
                    <select
                      required
                      className="input-field mt-1"
                      value={serviceForm.serviceCategory}
                      onChange={(e) => {
                        const category = e.target.value
                        setServiceForm(prev => ({ 
                          ...prev, 
                          serviceCategory: category,
                          // Automatically set payment type based on category
                          paymentType: category === 'SUBSCRIPTION' ? 'recurring' : 'one_off',
                          // Clear irrelevant fields when changing category
                          billingCycleDays: category === 'SUBSCRIPTION' ? prev.billingCycleDays : '',
                          predictedTimeline: category === 'PROJECT' ? prev.predictedTimeline : '',
                          setupTimeDays: category === 'PRODUCT' ? prev.setupTimeDays : ''
                        }))
                      }}
                    >
                      <option value="SUBSCRIPTION">Subscription</option>
                      <option value="PROJECT">Project</option>
                      <option value="PRODUCT">Product</option>
                    </select>
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700">Description</label>
                  <textarea
                    className="input-field mt-1"
                    rows={3}
                    value={serviceForm.description}
                    onChange={(e) => setServiceForm(prev => ({ ...prev, description: e.target.value }))}
                  />
                </div>
                
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Price (£)</label>
                    <input
                      type="number"
                      step="0.01"
                      required
                      className="input-field mt-1"
                      value={serviceForm.priceGbp}
                      onChange={(e) => setServiceForm(prev => ({ ...prev, priceGbp: e.target.value }))}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Billing Cycle (days)</label>
                    <input
                      type="number"
                      className={`input-field mt-1 ${serviceForm.serviceCategory !== 'SUBSCRIPTION' || serviceForm.paymentType !== 'recurring' ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                      value={serviceForm.billingCycleDays}
                      onChange={(e) => setServiceForm(prev => ({ ...prev, billingCycleDays: e.target.value }))}
                      placeholder={serviceForm.serviceCategory === 'SUBSCRIPTION' && serviceForm.paymentType === 'recurring' ? 'e.g. 28 for monthly' : 'N/A'}
                      disabled={serviceForm.serviceCategory !== 'SUBSCRIPTION' || serviceForm.paymentType !== 'recurring'}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Predicted Timeline (days)</label>
                    <input
                      type="number"
                      className={`input-field mt-1 ${serviceForm.serviceCategory !== 'PROJECT' ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                      value={serviceForm.predictedTimeline}
                      onChange={(e) => setServiceForm(prev => ({ ...prev, predictedTimeline: e.target.value }))}
                      placeholder={serviceForm.serviceCategory === 'PROJECT' ? 'e.g. 30 for a 30-day project' : 'N/A'}
                      disabled={serviceForm.serviceCategory !== 'PROJECT'}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Setup Time (days)</label>
                    <input
                      type="number"
                      className={`input-field mt-1 ${serviceForm.serviceCategory !== 'PRODUCT' ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                      value={serviceForm.setupTimeDays}
                      onChange={(e) => setServiceForm(prev => ({ ...prev, setupTimeDays: e.target.value }))}
                      placeholder={serviceForm.serviceCategory === 'PRODUCT' ? 'e.g. 2 for logo design' : 'N/A'}
                      disabled={serviceForm.serviceCategory !== 'PRODUCT'}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Sort Order</label>
                    <input
                      type="number"
                      className="input-field mt-1"
                      value={serviceForm.sortOrder}
                      onChange={(e) => setServiceForm(prev => ({ ...prev, sortOrder: e.target.value }))}
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Payment Type</label>
                    <select
                      className={`input-field mt-1 ${serviceForm.serviceCategory !== 'SUBSCRIPTION' ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                      value={serviceForm.paymentType}
                      onChange={(e) => setServiceForm(prev => ({ ...prev, paymentType: e.target.value }))}
                      disabled={serviceForm.serviceCategory !== 'SUBSCRIPTION'}
                    >
                      <option value="recurring">Recurring</option>
                      <option value="one_off">One-off</option>
                    </select>
                  </div>
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="isDeletable"
                      className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                      checked={serviceForm.isDeletable}
                      onChange={(e) => setServiceForm(prev => ({ ...prev, isDeletable: e.target.checked }))}
                    />
                    <label htmlFor="isDeletable" className="ml-2 block text-sm text-gray-900">
                      Allow deletion
                    </label>
                  </div>
                </div>
                
                <div className="flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={() => {
                      setShowServiceForm(false)
                      setEditingService(null)
                      setServiceForm({
                        name: '',
                        description: '',
                        serviceType: '',
                        serviceCategory: 'SUBSCRIPTION',
                        priceGbp: '',
                        billingCycleDays: '28',
                        predictedTimeline: '',
                        setupTimeDays: '',
                        features: {},
                        sortOrder: '0',
                        paymentType: 'recurring',
                        isDeletable: true
                      })
                    }}
                    className="btn-secondary"
                  >
                    Cancel
                  </button>
                  <button type="submit" className="btn-primary">
                    {editingService ? 'Update Service' : 'Create Service'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Service Assignment Form Modal */}
      {showAssignmentForm && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-medium text-gray-900">Assign Service to Client</h3>
                <button
                  onClick={() => setShowAssignmentForm(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              
              <form onSubmit={handleAssignService} className="space-y-4">
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Client Organization</label>
                    <select
                      required
                      className="input-field mt-1"
                      value={assignmentForm.clientOrgId}
                      onChange={(e) => setAssignmentForm(prev => ({ ...prev, clientOrgId: e.target.value }))}
                    >
                      <option value="">Select Organization</option>
                      {clientOrganizations
                        .filter(org => org.isActive)
                        .map(org => (
                          <option key={org.id} value={org.id}>
                            {org.name} ({org.domain})
                          </option>
                        ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Service</label>
                    <select
                      required
                      className="input-field mt-1"
                      value={assignmentForm.serviceId}
                      onChange={(e) => setAssignmentForm(prev => ({ ...prev, serviceId: e.target.value }))}
                    >
                      <option value="">Select Service</option>
                      {services
                        .filter(service => service.isActive)
                        .map(service => (
                          <option key={service.id} value={service.id}>
                            {service.name} - {formatPrice(service.priceGbp)} ({service.serviceCategory})
                          </option>
                        ))}
                    </select>
                  </div>
                </div>
                
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Start Date</label>
                    <input
                      type="date"
                      required
                      className="input-field mt-1"
                      value={assignmentForm.startDate}
                      onChange={(e) => setAssignmentForm(prev => ({ ...prev, startDate: e.target.value }))}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">End Date (Optional)</label>
                    <input
                      type="date"
                      className="input-field mt-1"
                      value={assignmentForm.endDate}
                      onChange={(e) => setAssignmentForm(prev => ({ ...prev, endDate: e.target.value }))}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Price Variation (Optional)</label>
                    <select
                      className="input-field mt-1 w-full"
                      value={assignmentForm.servicePriceId}
                      onChange={(e) => {
                        const val = e.target.value
                        if (val === '__add_price__') {
                          setShowPriceModal(true)
                          // keep current selection
                          return
                        }
                        setAssignmentForm(prev => ({ ...prev, servicePriceId: val }))
                      }}
                      disabled={!assignmentForm.serviceId}
                    >
                      <option value="">
                        {(() => {
                          const tmpl = services.find((s: any) => s.id === assignmentForm.serviceId)
                          if (!tmpl) return 'Template price'
                          const price = typeof tmpl.priceGbp === 'string' ? parseFloat(tmpl.priceGbp) : tmpl.priceGbp
                          const days = tmpl.billingCycleDays || 28
                          return `Default Price - £${price?.toFixed?.(2) ?? price}`
                        })()}
                      </option>
                      {servicePrices.map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.nickname ? `${p.nickname} - ` : ''}£{parseFloat(p.priceGbp).toFixed(2)}{p.billingCycleDays ? ` / ${p.billingCycleDays}d` : ''}
                        </option>
                      ))}
                      <option value="__add_price__">+ Add new price…</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Total Cycles</label>
                    <input
                      type="number"
                      className="input-field mt-1"
                      value={assignmentForm.totalCycles}
                      onChange={(e) => setAssignmentForm(prev => ({ ...prev, totalCycles: e.target.value }))}
                    />
                  </div>
                </div>
                
                <div className="flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={() => setShowAssignmentForm(false)}
                    className="btn-secondary"
                  >
                    Cancel
                  </button>
                  <button type="submit" className="btn-primary">
                    Assign Service
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Edit Cycles Modal */}
      {showEditCycles && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Update Total Cycles</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Total Cycles</label>
                  <input
                    type="number"
                    className="input-field mt-1"
                    value={editCyclesForm.totalCycles}
                    onChange={(e) => setEditCyclesForm(prev => ({ ...prev, totalCycles: e.target.value }))}
                    min={1}
                    required
                  />
                </div>
                <div className="flex justify-end gap-3">
                  <button className="btn-secondary" onClick={() => setShowEditCycles(false)}>Cancel</button>
                  <button
                    className="btn-primary"
                    onClick={async () => {
                      try {
                        await axios.put(`/api/services/client-services/${editCyclesForm.id}`, {
                          totalCycles: parseInt(editCyclesForm.totalCycles)
                        })
                        toast.success('Total cycles updated')
                        setShowEditCycles(false)
                        fetchData()
                      } catch (error: any) {
                        toast.error(error.response?.data?.error || 'Failed to update cycles')
                      }
                    }}
                  >
                    Save
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add Price Variation Modal */}
      {showPriceModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Add Price Variation</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Nickname (optional)</label>
                  <input
                    type="text"
                    className="input-field mt-1"
                    value={priceForm.nickname}
                    onChange={(e) => setPriceForm(prev => ({ ...prev, nickname: e.target.value }))}
                    placeholder="e.g. Legacy plan, VIP, Annual"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Price (£)</label>
                  <input
                    type="number"
                    step="0.01"
                    className="input-field mt-1"
                    value={priceForm.priceGbp}
                    onChange={(e) => setPriceForm(prev => ({ ...prev, priceGbp: e.target.value }))}
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Billing Cycle (days, optional)</label>
                  <input
                    type="number"
                    className="input-field mt-1"
                    value={priceForm.billingCycleDays}
                    onChange={(e) => setPriceForm(prev => ({ ...prev, billingCycleDays: e.target.value }))}
                    placeholder="e.g. 28, 30"
                  />
                </div>
                <div className="flex justify-end gap-3">
                  <button className="btn-secondary" onClick={() => setShowPriceModal(false)}>Cancel</button>
                  <button
                    className="btn-primary"
                    onClick={async () => {
                      try {
                        if (!assignmentForm.serviceId) return
                        const res = await axios.post(`/api/services/services/${assignmentForm.serviceId}/prices`, {
                          nickname: priceForm.nickname || undefined,
                          priceGbp: priceForm.priceGbp,
                          billingCycleDays: priceForm.billingCycleDays ? parseInt(priceForm.billingCycleDays) : undefined,
                          isActive: true
                        })
                        if (res.data.success) {
                          toast.success('Price variation added')
                          setShowPriceModal(false)
                          setPriceForm({ nickname: '', priceGbp: '', billingCycleDays: '' })
                          // Reload price list and select the new one
                          const list = await axios.get(`/api/services/services/${assignmentForm.serviceId}/prices`)
                          if (list.data.success) {
                            setServicePrices(list.data.data || [])
                            setAssignmentForm(prev => ({ ...prev, servicePriceId: res.data.data.id }))
                          }
                        }
                      } catch (error: any) {
                        toast.error(error.response?.data?.error || 'Failed to add price')
                      }
                    }}
                  >
                    Save
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Lifecycle Management Tab */}
      {activeTab === 'lifecycle' && (
        <div className="mt-8 space-y-6">
          {/* Analytics Overview */}
          {lifecycleAnalytics && (
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
              <div className="card">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className="w-8 h-8 bg-blue-100 rounded-md flex items-center justify-center">
                      <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                      </svg>
                    </div>
                  </div>
                  <div className="ml-4">
                    <h3 className="text-lg font-medium text-gray-900">Total Services</h3>
                    <p className="text-2xl font-semibold text-gray-900">{lifecycleAnalytics.totalServices}</p>
                    <p className="text-sm text-gray-500">All service assignments</p>
                  </div>
                </div>
              </div>

              <div className="card">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className="w-8 h-8 bg-green-100 rounded-md flex items-center justify-center">
                      <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                  </div>
                  <div className="ml-4">
                    <h3 className="text-lg font-medium text-gray-900">Active Services</h3>
                    <p className="text-2xl font-semibold text-gray-900">{lifecycleAnalytics.activeServices}</p>
                    <p className="text-sm text-gray-500">Currently running</p>
                  </div>
                </div>
              </div>

              <div className="card">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className="w-8 h-8 bg-yellow-100 rounded-md flex items-center justify-center">
                      <svg className="w-5 h-5 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                      </svg>
                    </div>
                  </div>
                  <div className="ml-4">
                    <h3 className="text-lg font-medium text-gray-900">Expiring Soon</h3>
                    <p className="text-2xl font-semibold text-gray-900">{lifecycleAnalytics.expiringSoon}</p>
                    <p className="text-sm text-gray-500">Within 7 days</p>
                  </div>
                </div>
              </div>

              <div className="card">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className="w-8 h-8 bg-red-100 rounded-md flex items-center justify-center">
                      <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </div>
                  </div>
                  <div className="ml-4">
                    <h3 className="text-lg font-medium text-gray-900">Expired Services</h3>
                    <p className="text-2xl font-semibold text-gray-900">{lifecycleAnalytics.expiredServices}</p>
                    <p className="text-sm text-gray-500">Need renewal</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Expiring Services */}
          <div className="card">
            <div className="px-4 py-5 sm:p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">Services Expiring Soon</h3>
                <span className="text-sm text-gray-500">{expiringServices.length} services</span>
              </div>
              {expiringServices.length > 0 ? (
                <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
                  <table className="min-w-full divide-y divide-gray-300">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Client</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Service</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Days Remaining</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Current Cycle</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Next Billing</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {expiringServices.map((service) => (
                        <tr key={service.id}>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            {service.clientOrg.name}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {service.name}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                              (service as any).daysRemaining <= 3 ? 'bg-red-100 text-red-800' :
                              (service as any).daysRemaining <= 7 ? 'bg-yellow-100 text-yellow-800' :
                              'bg-green-100 text-green-800'
                            }`}>
                              {(service as any).daysRemaining || 0} days
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {service.currentCycle}/{service.totalCycles}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {(service as any).nextBillingDate ? new Date((service as any).nextBillingDate).toLocaleDateString() : 'N/A'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                            <div className="flex space-x-2">
                              <button
                                onClick={() => handleRenewService(service.id)}
                                className="text-green-600 hover:text-green-900"
                              >
                                Renew
                              </button>
                              <button
                                onClick={() => handleAdvanceCycle(service.id)}
                                className="text-blue-600 hover:text-blue-900"
                              >
                                Advance Cycle
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-8">
                  <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <h3 className="mt-2 text-sm font-medium text-gray-900">No services expiring soon</h3>
                  <p className="mt-1 text-sm text-gray-500">All services are in good standing.</p>
                </div>
              )}
            </div>
          </div>

          {/* Services Needing Renewal */}
          <div className="card">
            <div className="px-4 py-5 sm:p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">Services Needing Renewal</h3>
                <span className="text-sm text-gray-500">{servicesNeedingRenewal.length} services</span>
              </div>
              {servicesNeedingRenewal.length > 0 ? (
                <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
                  <table className="min-w-full divide-y divide-gray-300">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Client</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Service</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Renewal Status</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {servicesNeedingRenewal.map((service) => (
                        <tr key={service.id}>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            {service.clientOrg.name}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {service.name}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                              service.status === 'active' ? 'bg-green-100 text-green-800' :
                              service.status === 'expired' ? 'bg-red-100 text-red-800' :
                              service.status === 'suspended' ? 'bg-yellow-100 text-yellow-800' :
                              'bg-gray-100 text-gray-800'
                            }`}>
                              {service.status}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {(service as any).renewalStatus || 'pending'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                            <div className="flex space-x-2">
                              <button
                                onClick={() => handleRenewService(service.id)}
                                className="text-green-600 hover:text-green-900"
                              >
                                Renew
                              </button>
                              <button
                                onClick={() => handleUpdateCycle(service.id)}
                                className="text-blue-600 hover:text-blue-900"
                              >
                                Update Cycle
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-8">
                  <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <h3 className="mt-2 text-sm font-medium text-gray-900">No services needing renewal</h3>
                  <p className="mt-1 text-sm text-gray-500">All services are up to date.</p>
                </div>
              )}
            </div>
          </div>

          {/* Cron Job Management */}
          {cronStatus && (
            <div className="card">
              <div className="px-4 py-5 sm:p-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Automated Tasks</h3>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                  <div className="text-center">
                    <div className="text-2xl font-semibold text-gray-900">Daily Updates</div>
                    <div className="text-sm text-gray-500">2:00 AM GMT</div>
                    <div className="mt-2">
                      <button
                        onClick={handleTriggerDailyUpdate}
                        className="text-sm text-blue-600 hover:text-blue-900"
                      >
                        Trigger Now
                      </button>
                    </div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-semibold text-gray-900">Expiration Check</div>
                    <div className="text-sm text-gray-500">Every 6 hours</div>
                    <div className="mt-2">
                      <button
                        onClick={handleTriggerExpirationCheck}
                        className="text-sm text-blue-600 hover:text-blue-900"
                      >
                        Trigger Now
                      </button>
                    </div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-semibold text-gray-900">Weekly Report</div>
                    <div className="text-sm text-gray-500">Mondays 9:00 AM</div>
                    <div className="mt-2">
                      <button
                        onClick={handleTriggerWeeklyReport}
                        className="text-sm text-blue-600 hover:text-blue-900"
                      >
                        Trigger Now
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default ServiceManagement

